import React from "react";
import _ from "lodash";
import numeral from "numeral";
import moment from "moment-timezone";
import { NetworkAPI, UnitAPI } from "apis";

import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import UprPriceSection from "components/network/pricing-rules/gam-upr/UprPriceSection";
import HighLightRowWrapper from "components/common/HighLightRowWrapper";
import UprTargetingPopover from "components/ops-mgmt/account/custom-upr/UprTargetingPopover";
import UprName from "components/common/UprName";
import SortButton from "components/common/SortButton";
import AppliedBaseModal from "./AppliedBaseModal";

import {
  getAppliedGeneralChartOptions,
  getAppliedUprSubChartOptions,
} from "components/anatomy-cst/AnatomyCstChartHelper";
import {
  APPLIED_CHART_TYPES,
  parseMetaData,
  getDataByDate,
  getDataByExternalId,
  getAllOptions,
  thClass,
} from "components/anatomy-cst/AppliedDataHelper";

import { getCurrencySymbol } from "helpers/Utils";
import { normalizeRevenue } from "helpers/Chart";
import { tdClass } from "helpers/StyleClass";
import { transformGamUprToUprConfig } from "components/network/pricing-rules/gam-upr/GamUprHelper";
import { setQueryString } from "helpers/QueryString";
import { getDateList } from "helpers/DateTransform";
import { SORT_DIRECTIONS } from "constants/UI";

import { FiFileText } from "react-icons/fi";

class AppliedUprModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errMsg: null,
      isLoading: false,
      popupId: "",
      hoverIndex: "",
      startDate: "",
      endDate: "",
      apiParam: {},
      dataParam: {},
      sortKey: "",
      sortOrder: SORT_DIRECTIONS.DESC,

      tableData: [],
      selectedItems: [],
      dateList: [],
      transformedRules: [],
      originData: null,

      mainChartDataOptions: {},
      subChartDataOptions: {},

      selectedTab: null,
      tabOptions: [],

      mainChartSelected: null,
      mainChartOptions: [],

      subChartSelected: null,
      subChartOptions: [],

      networkInfo: null,
      units: null,
      virtualPlacements: null,
      protectedUprIds: null,
      preservedUprIds: null,
      resourceById: null,
      segmentData: null,
      customData: null,
    };

    this.parseDataFormat = this.parseDataFormat.bind(this);

    this.handleChartReset = this.handleChartReset.bind(this);
    this.handleChartSelected = this.handleChartSelected.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
    this.handlePopup = this.handlePopup.bind(this);
    this.handleSelectItem = this.handleSelectItem.bind(this);
    this.handleSelectItemAll = this.handleSelectItemAll.bind(this);
    this.handleImportSelected = this.handleImportSelected.bind(this);
  }

  async componentDidMount() {
    const { networkId, startDate, includeToday = true } = this.props;

    try {
      this.setState({ isLoading: true });

      if (_.isNil(networkId)) {
        throw new Error("Network ID is required to fetch data.");
      }

      if (_.isNil(startDate)) {
        throw new Error("Start date is required to fetch data.");
      }

      const [
        networkInfo,
        units,
        virtualPlacements,
        { upr = {} },
        { config },
        { config: segmentData },
        { customSettings: customData },
      ] = await Promise.all([
        NetworkAPI.getNetworkInfo({ networkId }),
        NetworkAPI.getUnitsByNetworkId({ networkId }),
        NetworkAPI.getNetworkVirtualPlacements({ networkId }),
        NetworkAPI.getNetworkResourceMapping({ networkId }),
        NetworkAPI.getRawNetworkFeatures({ networkId }),
        NetworkAPI.getNetworkUprSegmentation({ networkId }),
        NetworkAPI.getNetworkCustomUpr({ networkId }),
      ]);

      const protectedUprIds = _.get(config, "publisherUPRProtectionList", []);
      const preservedUprIds = _.get(config, "preservedUPRIds", []);

      const resourceById = _.keyBy(upr.mapping, (item) => {
        return item.id;
      });

      const endDate = includeToday
        ? moment().format("YYYY-MM-DD")
        : moment().subtract(1, "day").format("YYYY-MM-DD");

      await this.handleGetData({
        startDate: startDate.split(" ")[0],
        endDate,
        networkInfo,
        units,
        virtualPlacements,
        protectedUprIds,
        preservedUprIds,
        resourceById,
        segmentData,
        customData,
        dataParam: {
          startDate: startDate.split(" ")[0],
          endDate,
        },
      });
    } catch (error) {
      this.setState({
        errMsg: error.message,
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  parseDataFormat(data, type) {
    const { networkInfo } = this.state;
    let result = "";
    if (type === APPLIED_CHART_TYPES.REVENUE) {
      result =
        getCurrencySymbol(networkInfo.currency) +
        numeral(data).format("0,0.00");
    } else if (type === APPLIED_CHART_TYPES.IMPRESSION) {
      result = numeral(data).format("0,0");
    } else if (type === APPLIED_CHART_TYPES.ECPM) {
      result =
        getCurrencySymbol(networkInfo.currency) +
        numeral(data).format("0,0.00");
    }
    return result;
  }

  handleChartReset() {
    const { dataParam } = this.state;
    this.handleGetData({ ...dataParam });
  }

  async handleChartSelected({ min, max, pos }) {
    let selectedDates = [];
    const { dateList } = this.state;

    if (!_.isNil(min) && !_.isNil(max)) {
      const startIndex = Math.ceil(min);
      const endIndex = Math.floor(max);
      selectedDates = dateList.slice(startIndex, endIndex + 1);
    } else if (!_.isNil(pos)) {
      selectedDates = [dateList[pos], dateList[pos]];
    }

    if (selectedDates.length === 0) return;
    this.setState({ isLoading: true });

    try {
      await this.handleGetData({
        startDate: selectedDates[0],
        endDate: selectedDates[selectedDates.length - 1],
      });
    } catch (error) {
      this.setState({
        errMsg: error.message,
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async handleGetData({
    startDate = this.state.startDate,
    endDate = this.state.endDate,
    networkInfo = this.state.networkInfo,
    units = this.state.units,
    virtualPlacements = this.state.virtualPlacements,
    protectedUprIds = this.state.protectedUprIds,
    preservedUprIds = this.state.preservedUprIds,
    resourceById = this.state.resourceById,
    segmentData = this.state.segmentData,
    customData = this.state.customData,
    dataParam,
  }) {
    const { unitId } = this.props;

    const apiStartDate = _.get(this.state, "apiParam.startDate");
    const isFetchData =
      _.isNil(apiStartDate) || moment(startDate).isBefore(apiStartDate);

    let reports = [];
    let transformedRules = [];

    if (isFetchData) {
      const result = await UnitAPI.getAppliedUprReport({
        unitId,
        startDate,
      });

      reports = result.reports || [];

      transformedRules = _.map(_.values(result.props), (upr) => {
        const newUpr = transformGamUprToUprConfig({ gamUpr: upr });

        newUpr.__isI2wRule = _.startsWith(upr.uprName, "Intowow_");
        newUpr.__isPubRule = !_.startsWith(upr.uprName, "Intowow_");
        newUpr.__isProtected =
          _.indexOf(protectedUprIds, _.parseInt(upr.externalId)) !== -1;
        newUpr.__isPreserved =
          _.indexOf(preservedUprIds, _.parseInt(upr.externalId)) !== -1;

        const props = _.get(resourceById, [upr.externalId, "props"], null);

        return {
          ...newUpr,
          props,
          segment:
            props && props.type === "UPR_SEGMENTATION"
              ? _.find(segmentData, {
                  id: props.segmentId,
                })
              : null,
          custom:
            props && props.type === "CUSTOM"
              ? _.find(customData, {
                  id: props.id,
                })
              : null,
        };
      });
    } else {
      reports = this.state.originData || [];
      transformedRules = this.state.transformedRules || [];
    }

    const dateList = getDateList(startDate, endDate, true);

    const filteredReports = _.filter(reports, (report) =>
      dateList.includes(report.date)
    ).map((item) => ({
      ...item,
      revenue: normalizeRevenue(item.revenue),
    }));

    const groupByType = _.groupBy(filteredReports, (item) => item.group_type);

    const typeList = _.keys(groupByType);

    const { tabOptions, mainChartOptions, subChartOptions } = getAllOptions({
      typeList,
    });

    const metaData = parseMetaData({
      data: groupByType,
      dateList,
    });

    const groupData = getDataByDate({
      data: metaData,
    });

    const tableData = getDataByExternalId({
      data: metaData,
      typeList,
      transformedRules,
    });

    const isDateRangeChanged = !_.isEqual(dataParam || this.state.dataParam, {
      startDate,
      endDate,
    });

    const mainChartDataOptions = {};
    const subChartDataOptions = {};
    _.forEach(tabOptions, (tab) => {
      _.forEach(mainChartOptions, (chart, idx) => {
        _.set(
          mainChartDataOptions,
          [tab.value, chart.value],
          getAppliedGeneralChartOptions({
            xAxis: dateList,
            dataList: tab.groupList.map((type) => ({
              name: `${type}`,
              data: _.get(groupData, [type, chart.value], []),
            })),
            chartType: chart.value,
            currency: networkInfo.currency,
            selectFn: this.handleChartSelected,
            resetFn: isDateRangeChanged ? this.handleChartReset : null,
          })
        );

        _.set(
          subChartDataOptions,
          [tab.value, chart.value],
          tab.groupList.map((type) =>
            getAppliedUprSubChartOptions({
              xAxis: dateList,
              dataList: _.filter(tableData, (item) => item.data[type]).map(
                (item) => ({
                  name: item.externalId,
                  data: _.get(item, ["data", type, chart.value, "list"], []),
                  ratio: _.get(
                    item,
                    ["data", type, chart.value, "ratioByDate"],
                    []
                  ),
                })
              ),
              chartType: chart.value,
              currency: networkInfo.currency,
              title: tab.groupList.length > 1 ? type : "",
            })
          )
        );
      });
    });

    const stateData = {};

    if (isFetchData) {
      stateData.apiParam = {
        startDate,
        endDate,
      };
    }

    if (!_.isNil(dataParam)) {
      stateData.dataParam = dataParam;
    }

    const selectedTabIdx = _.findIndex(
      tabOptions,
      (option) => option.value === this.state.selectedTab
    );
    if (selectedTabIdx < 0 && tabOptions.length > 0) {
      stateData.selectedTab = _.get(tabOptions, "[0].value", null);
    }

    const sortIdx = selectedTabIdx < 0 ? 0 : selectedTabIdx;
    stateData.sortKey = `${_.get(tabOptions, [sortIdx, "groupList", 0], "")}.${
      this.state.sortKey.split(".")[1] || APPLIED_CHART_TYPES.IMPRESSION
    }`;

    const selectedMainIdx = _.findIndex(
      mainChartOptions,
      (option) => option.value === this.state.mainChartSelected
    );
    if (selectedMainIdx < 0 && mainChartOptions.length > 0) {
      stateData.mainChartSelected = _.get(mainChartOptions, "[0].value", null);
    }

    const selectedSubIdx = _.findIndex(
      subChartOptions,
      (option) => option.value === this.state.subChartSelected
    );
    if (selectedSubIdx < 0 && subChartOptions.length > 0) {
      stateData.subChartSelected = _.get(subChartOptions, "[0].value", null);
    }

    this.setState({
      ...stateData,
      startDate,
      endDate,

      transformedRules,
      originData: reports,
      dateList,
      networkInfo,
      units,
      virtualPlacements,
      protectedUprIds,
      preservedUprIds,
      resourceById,
      segmentData,
      customData,

      tableData,

      tabOptions,

      mainChartOptions,

      subChartOptions,

      mainChartDataOptions,
      subChartDataOptions,

      selectedItems: [],
    });
  }

  handlePopup(externalId) {
    this.setState({ popupId: externalId });
  }

  handleSelectItem(id) {
    const { selectedItems } = this.state;

    this.setState({
      selectedItems:
        _.indexOf(selectedItems, id) >= 0
          ? _.filter(selectedItems, (item) => item !== id)
          : [...selectedItems, id],
    });
  }

  handleSelectItemAll(list) {
    this.setState({
      selectedItems: list,
    });
  }

  handleImportSelected() {
    const { selectedItems, networkInfo } = this.state;
    const queryString = setQueryString(
      this.props,
      { importId: selectedItems },
      true
    );
    window.open(
      `${window.location.origin}/#/ops-mgmt/account/network/${networkInfo.networkId}/upr-setting?${queryString}`
    );
  }

  render() {
    const { unitId, unitName, handleClose } = this.props;

    const {
      dataParam,
      selectedItems,
      networkInfo,
      units,
      virtualPlacements,
      popupId,
      hoverIndex,
      sortKey,
      sortOrder,

      tableData,

      tabOptions,
      selectedTab,

      mainChartOptions,
      mainChartSelected,
      subChartOptions,
      subChartSelected,

      mainChartDataOptions,
      subChartDataOptions,

      isLoading,
      errMsg,
    } = this.state;

    const parseTab = selectedTab
      ? _.find(tabOptions, { value: selectedTab })
      : {};

    const filterData = _.filter(tableData, ({ data }) =>
      _.some(parseTab.groupList, (groupType) => data[groupType])
    );

    const isSelectAll =
      filterData.length > 0 && selectedItems.length === filterData.length;

    const parsedSortKey =
      sortKey === "" || sortKey === "externalId"
        ? sortKey
        : `data.${sortKey}.amount`;

    const sortTableData = _.orderBy(
      filterData,
      parsedSortKey !== "" ? [(d) => _.get(d, parsedSortKey, 0)] : [],
      [sortOrder]
    );

    return (
      <AppliedBaseModal
        onClose={handleClose}
        title={{
          label: "Applied UPR ",
          highlight:
            dataParam && dataParam.startDate && dataParam.endDate
              ? `${unitId || ""} - ${unitName || ""} [${
                  dataParam.startDate
                } ~ ${dataParam.endDate}]`
              : "",
        }}
        isLoading={isLoading}
        errMsg={errMsg}
        isEmptyData={_.isEmpty(tableData)}
        tabOptions={tabOptions}
        selectedTab={selectedTab}
        mainChartOptions={mainChartOptions}
        mainChartSelected={mainChartSelected}
        subChartOptions={subChartOptions}
        subChartSelected={subChartSelected}
        mainChartData={mainChartDataOptions}
        subChartData={subChartDataOptions}
        filterData={filterData}
        selectedItems={selectedItems}
        handleTabChanged={(option) =>
          this.setState({
            selectedTab: option.value,
            sortKey:
              sortKey !== ""
                ? `${option.groupList[0]}.${
                    sortKey.split(".")[1] || APPLIED_CHART_TYPES.IMPRESSION
                  }`
                : "",
          })
        }
        handleMainChartChanged={(mainChartSelected) =>
          this.setState({
            mainChartSelected,
          })
        }
        handleSubChartChanged={(subChartSelected) =>
          this.setState({
            subChartSelected,
          })
        }
        handleImportSelected={this.handleImportSelected}
      >
        {!_.isEmpty(parseTab) && (
          <table className="table w-full table-auto text-sm">
            <thead className="border-b bg-gray-200 text-xs">
              <tr>
                <th className={thClass + " text-center"} colSpan={4}></th>
                {parseTab.groupList.map((groupType) => (
                  <th
                    className={`${
                      hoverIndex === groupType ? "bg-yellow-200" : ""
                    } ${thClass} text-center`}
                    key={groupType}
                    colSpan={3}
                    onMouseEnter={() =>
                      this.setState({ hoverIndex: groupType })
                    }
                    onMouseLeave={() => this.setState({ hoverIndex: "" })}
                  >
                    {groupType}
                  </th>
                ))}
              </tr>
              <tr>
                <th className={thClass + " w-12 text-center"}>
                  <input
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "4px",
                    }}
                    type="checkbox"
                    checked={isSelectAll}
                    onChange={() =>
                      this.handleSelectItemAll(
                        isSelectAll
                          ? []
                          : filterData.map((item) => item.externalId)
                      )
                    }
                  ></input>
                </th>
                <th className={thClass + " w-32"}>Unit</th>
                <th className={thClass + " w-40 text-right"}>Price Section</th>
                <th className={thClass + " w-12"}></th>
                {parseTab.groupList.map((gType) => (
                  <React.Fragment key={gType}>
                    {_.values(APPLIED_CHART_TYPES).map((dataType) => (
                      <th
                        className={`${
                          hoverIndex === gType ? "bg-yellow-200" : ""
                        } ${thClass} text-right`}
                        key={`th-${gType}-${dataType}`}
                      >
                        <div className="flex items-center">
                          {dataType}
                          <SortButton
                            sortKey={`${gType}.${dataType}`}
                            currentSortKey={sortKey}
                            currentSortOrder={sortOrder}
                            onClick={({ sortKey = "", sortOrder = "" }) =>
                              this.setState({ sortKey, sortOrder })
                            }
                          />
                        </div>
                      </th>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortTableData.map((item, index) => {
                const gamLink = `https://admanager.google.com/${
                  networkInfo ? networkInfo.code : ""
                }#inventory/pricing/detail/pricing_id=${
                  item.externalId
                }&tab=settings`;

                return (
                  <HighLightRowWrapper
                    selected={popupId === item.externalId}
                    key={`${item.externalId}-${index}`}
                  >
                    <td className={tdClass + " text-center"}>
                      <input
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "4px",
                        }}
                        type="checkbox"
                        checked={
                          _.indexOf(selectedItems, item.externalId) !== -1
                        }
                        onChange={() => this.handleSelectItem(item.externalId)}
                      ></input>
                    </td>
                    <td className={tdClass}>
                      <div className="flex items-center justify-between">
                        <span
                          className={`font-mono ${
                            popupId === item.externalId ? "font-semibold" : ""
                          }`}
                        >
                          {item.externalId}
                        </span>

                        <div className="flex items-center text-blue-600">
                          <ClickToCopyWrapper
                            copyText={gamLink}
                            tooltipMessage="Click to copy GAM link"
                          ></ClickToCopyWrapper>
                        </div>
                      </div>
                      <div
                        className={`${
                          popupId === item.externalId ? "font-semibold" : ""
                        }`}
                      >
                        <UprName item={item}></UprName>
                      </div>
                    </td>
                    <td className={tdClass + " text-right"}>
                      <UprPriceSection
                        item={item.priceSection}
                        currency={item.priceSection.currency}
                        networkId={networkInfo.networkId}
                        onOpen={() => this.handlePopup(item.externalId)}
                        onClose={() => this.handlePopup("")}
                      ></UprPriceSection>
                    </td>
                    <td className={tdClass + " text-center"}>
                      <div>
                        {item.targetingSection && (
                          <UprTargetingPopover
                            id={item.externalId}
                            name={item.uprName}
                            place="left"
                            networkId={networkInfo.networkId}
                            units={units}
                            virtualPlacements={virtualPlacements}
                            targetingSection={item.targetingSection}
                            triggerElement={
                              <div className="cursor-pointer  text-blue-600 hover:text-blue-700">
                                <FiFileText />
                              </div>
                            }
                            onOpen={() => this.handlePopup(item.externalId)}
                            onClose={() => this.handlePopup("")}
                          ></UprTargetingPopover>
                        )}
                      </div>
                    </td>
                    {parseTab.groupList.map((gType) => (
                      <React.Fragment key={gType}>
                        {_.values(APPLIED_CHART_TYPES).map((dataType) => (
                          <td
                            className={`${tdClass} text-right font-mono ${
                              hoverIndex === gType ? "bg-yellow-100" : ""
                            }`}
                            key={`td-${gType}-${dataType}`}
                          >
                            {dataType === APPLIED_CHART_TYPES.ECPM ? (
                              <>
                                {this.parseDataFormat(
                                  _.get(
                                    item,
                                    ["data", gType, dataType, "amount"],
                                    0
                                  ),
                                  dataType
                                )}
                              </>
                            ) : (
                              <>
                                {numeral(
                                  _.get(
                                    item,
                                    ["data", gType, dataType, "ratio"],
                                    0
                                  )
                                ).format("0.00%")}
                                <div className="text-xs text-gray-600">
                                  {this.parseDataFormat(
                                    _.get(
                                      item,
                                      ["data", gType, dataType, "amount"],
                                      0
                                    ),
                                    dataType
                                  )}
                                </div>
                              </>
                            )}
                          </td>
                        ))}
                      </React.Fragment>
                    ))}
                  </HighLightRowWrapper>
                );
              })}
            </tbody>
          </table>
        )}
      </AppliedBaseModal>
    );
  }
}

export default AppliedUprModal;
